import { canonicalDigest, canonicalJson, validateSha256 } from '@djgg/gameplay-kit';
import { RuntimeReleaseBlockedError } from './compositionRuntimeRelease.js';
import { artPlanAssetNames } from './design/artPlan.js';
import { acceptCompositionV1, compositionReceiptCoversV1, compositionReceiptItemsV1, validateAcceptedCompositionV1,
  type AcceptedCompositionV1, type CompositionMediaReceiptV1 } from './spec/gameSpec.js';
import { compositionAnimationSlotsV1, compositionBundleSlotsV1, compositionDeferralClosedV1 } from './spec/compositionPresentation.js';
import { COMPOSED_PROFILE } from './spec/composedSpec.js';
import { compositionRevisionCarryV1, compositionSlotBasisV1, type CompositionMediaCarryV1 } from './spec/compositionMediaDesign.js';
import type { BuildJob, CreationVersion } from './types.js';

/** The released runtime an accepted composition compiles on: its compiler,
 * ownership protocol, runtime closure and release. Everything else in an
 * authority (spec, intent, config, media) is the accepted game itself. */
export type CompositionRuntimeIdentityV1 = Pick<AcceptedCompositionV1,
  'compilerRevision' | 'compilerDigest' | 'ownershipRevision' | 'closureDigest' | 'runtimeReleaseId'>;

export function compositionRuntimeIdentityV1(authority: CompositionRuntimeIdentityV1): CompositionRuntimeIdentityV1 {
  return {
    compilerRevision: authority.compilerRevision,
    compilerDigest: authority.compilerDigest,
    ownershipRevision: authority.ownershipRevision,
    closureDigest: authority.closureDigest,
    ...(authority.runtimeReleaseId ? { runtimeReleaseId: authority.runtimeReleaseId } : {}),
  };
}

/** The same accepted game admitted on `runtime`: only the runtime identity
 * changes, and the game passes the running code's admission again. */
export async function compositionOnRuntimeV1(
  authority: AcceptedCompositionV1,
  runtime: CompositionRuntimeIdentityV1,
): Promise<AcceptedCompositionV1> {
  const { digest: _digest, compilerRevision: _compilerRevision, compilerDigest: _compilerDigest,
    ownershipRevision: _ownershipRevision, closureDigest: _closureDigest, runtimeReleaseId: _runtimeReleaseId,
    ...game } = authority;
  return acceptCompositionV1({ ...structuredClone(game), ...compositionRuntimeIdentityV1(runtime) });
}

/** Whether `successor` is exactly `predecessor`'s accepted game, admitted on
 * any runtime (its own, or a later release's). */
export async function isCompositionSuccessorV1(
  successor: AcceptedCompositionV1,
  predecessor: AcceptedCompositionV1,
): Promise<boolean> {
  try {
    return (await compositionOnRuntimeV1(successor, compositionRuntimeIdentityV1(predecessor))).digest === predecessor.digest;
  } catch {
    return false;
  }
}

/** THE successor admission: a fork or asset rebind of a published composed
 * game re-admits its unchanged accepted game on the current released runtime
 * `base` (the active release's configured authority), never on the release it
 * was first admitted with. A pinned compiler is executable only while it is
 * the running one, and a successor compiles afresh, so it must carry the
 * running release's identity. The accepted game must pass the running code's
 * deterministic admission (spec derivation, mechanics config, requirements,
 * media promise); the successor build then re-runs its proofs before publish.
 * A game that no longer admits is a typed, creator-readable precondition. */
export async function admitCompositionSuccessorV1(
  predecessor: unknown,
  base: AcceptedCompositionV1,
): Promise<AcceptedCompositionV1> {
  if (!base.runtimeReleaseId) {
    throw new RuntimeReleaseBlockedError('runtime-release-unpinned',
      'Released composition successors require the active released runtime');
  }
  let accepted: AcceptedCompositionV1;
  try {
    accepted = await validateAcceptedCompositionV1(predecessor);
  } catch (error) {
    throw new RuntimeReleaseBlockedError('runtime-admission-successor-unproved',
      `This game no longer passes the current composition release's admission `
      + `(${error instanceof Error ? error.message : String(error)}); make it again from its reviewed game`);
  }
  return compositionOnRuntimeV1(accepted, base);
}

function validateRuntimeIdentity(value: unknown): CompositionRuntimeIdentityV1 {
  const released = !!value && typeof value === 'object' && Object.hasOwn(value, 'runtimeReleaseId');
  exactRecord(value, ['compilerRevision', 'compilerDigest', 'ownershipRevision', 'closureDigest',
    ...(released ? ['runtimeReleaseId'] : [])], 'composition predecessor runtime');
  if (value.compilerRevision !== 'courier-compiler@1' || value.ownershipRevision !== 'closed-composition@1') {
    throw new Error('Invalid composition predecessor runtime');
  }
  validateSha256(value.compilerDigest, 'composition predecessor compiler');
  validateSha256(value.closureDigest, 'composition predecessor closure');
  if (released) validateSha256(value.runtimeReleaseId, 'composition predecessor release');
  return compositionRuntimeIdentityV1(value as unknown as CompositionRuntimeIdentityV1);
}

export interface CompositionPredecessorV1 {
  creationId: string;
  version: number;
  jobId: string;
  authorityDigest: string;
  /** The runtime the predecessor was admitted on. The successor's authority
   * is the predecessor's accepted game re-admitted on the current release, so
   * it differs from the predecessor's exactly in this identity. */
  runtime: CompositionRuntimeIdentityV1;
  sourcePath: string;
  sourceDigest: string;
  receiptDigest?: string;
}

export interface CompositionMediaRefV1 {
  slot: string;
  /** Namespace that owns the immutable variant row and bytes. Forks retain
   * the source creation namespace instead of pretending the variant was
   * generated for the target creation. */
  creationId: string;
  variantId: string;
  contentSha256: string;
  url: string;
}

/** Whether a variant row is the bytes a media reference names. A row's
 * identity is its recorded `contentSha256`; a LEGACY row, recorded before
 * ingestion hashed bytes, has none — its identity is the digest a trusted
 * build hashed from its stored bytes into a version's assets snapshot (the
 * reference). Whoever binds the row re-hashes the stored object against
 * that digest (the composition media join), so nothing is taken on trust. */
export function compositionVariantMatchesRefV1(variant: { contentSha256?: string | null }, ref: { contentSha256: string }): boolean {
  return variant.contentSha256 == null ? /^[0-9a-f]{64}$/.test(ref.contentSha256) : variant.contentSha256 === ref.contentSha256;
}

export type CompositionLineageMediaV1 =
  | { kind: 'coded' }
  | { kind: 'retained-final'; refs: CompositionMediaRefV1[] }
  | { kind: 'approved-variants'; refs: CompositionMediaRefV1[] };

/** Immutable successor authority. This is deliberately separate from
 * AcceptedCompositionV1: a fork/rebind action cannot rewrite accepted intent,
 * settings, design, mechanics or requirements. The successor's authority
 * (`authorityDigest`) is its predecessor's accepted game on the current
 * released runtime ({@link admitCompositionSuccessorV1}). */
export interface CompositionLineageV1 {
  schemaVersion: 1;
  /** `fork-copy`: a new creation's v1 copies a published game. `asset-rebind`:
   * the same creation binds approved replacement media. `presentation-republish`:
   * the same creation re-publishes its active game unchanged (retained final
   * media) so build-time presentation — platform title, current shell — is
   * current; it records `predecessor.version` as the version it republishes. */
  kind: 'fork-copy' | 'asset-rebind' | 'presentation-republish';
  /** The successor's own accepted authority. */
  authorityDigest: string;
  predecessor: CompositionPredecessorV1;
  media: CompositionLineageMediaV1;
  digest: string;
}

type CompositionLineageBodyV1 = Omit<CompositionLineageV1, 'digest'>;

/** The successor kinds that carry their predecessor's exact final media (and
 * its deferrals) rather than creator-approved replacements. */
export function compositionLineageRetainsFinalMediaV1(kind: CompositionLineageV1['kind']): boolean {
  return kind === 'fork-copy' || kind === 'presentation-republish';
}

/** The version build mode each successor kind is accepted and routed under. */
export function compositionLineageBuildModeV1(kind: CompositionLineageV1['kind']): 'copy' | 'asset-rebind' | 'presentation-republish' {
  return kind === 'fork-copy' ? 'copy' : kind;
}

function exactRecord(value: unknown, keys: readonly string[], label: string): asserts value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || ![Object.prototype, null].includes(Object.getPrototypeOf(value))
    || Reflect.ownKeys(value).length !== keys.length
    || Object.keys(value).sort().join(',') !== [...keys].sort().join(',')
    || Object.values(Object.getOwnPropertyDescriptors(value)).some(descriptor => !('value' in descriptor))) {
    throw new Error(`Invalid ${label}`);
  }
}

function nonEmpty(value: unknown, label: string): asserts value is string {
  if (typeof value !== 'string' || !value.length || value.length > 2048 || /[\u0000-\u001f]/.test(value)) {
    throw new Error(`Invalid ${label}`);
  }
}

function repositoryId(value: unknown, label: string): asserts value is string {
  nonEmpty(value, label);
  if (value.length > 200 || value === '.' || value === '..' || !/^[A-Za-z0-9_.-]+$/.test(value)) {
    throw new Error(`Invalid ${label}`);
  }
}

function assertPublishedSourceArchive(path: string, creationId: string, version: number, digest: string): void {
  const match = /^gs:\/\/([^/]+)\/([^/]+)\/([1-9][0-9]*)\/src\.([a-f0-9]{64})\.tgz$/.exec(path);
  const bucket = match?.[1] ?? '';
  if (!match || bucket.length < 3 || bucket.length > 63
    || !/^[a-z0-9][a-z0-9._-]*[a-z0-9]$/.test(bucket) || bucket.includes('..')
    || match[2] !== creationId || match[3] !== String(version) || match[4] !== digest) {
    throw new Error('Composition predecessor source archive identity changed');
  }
}

function validateRefs(
  refs: unknown,
  authority: AcceptedCompositionV1,
  expectedCreationId: string | undefined,
): CompositionMediaRefV1[] {
  // Lineage carries the bundle media; the gallery cover belongs to the
  // creation and is never inherited through an authority. A deferrable slot may be
  // absent: its predecessor deferred it, and the successor inherits that gap
  // (deriveCompositionLineageV1 proves it against the predecessor receipt).
  const slots = authority.media ? compositionBundleSlotsV1(authority.media.slots) : [];
  if (!Array.isArray(refs) || !authority.media || refs.length > slots.length) {
    throw new Error('Composition lineage media does not cover accepted slots');
  }
  const checked = refs.map((input): CompositionMediaRefV1 => {
    exactRecord(input, ['slot', 'creationId', 'variantId', 'contentSha256', 'url'], 'composition media reference');
    if (!slots.includes(input.slot as string)) throw new Error('Invalid composition media slot');
    repositoryId(input.creationId, 'composition media creation');
    repositoryId(input.variantId, 'composition media variant');
    nonEmpty(input.url, 'composition media URL');
    validateSha256(input.contentSha256, 'composition media');
    if (expectedCreationId && input.creationId !== expectedCreationId) {
      throw new Error('Composition media namespace changed');
    }
    return structuredClone(input) as unknown as CompositionMediaRefV1;
  });
  const actualSlots = checked.map(ref => ref.slot).sort();
  if (new Set(actualSlots).size !== actualSlots.length) throw new Error('Composition lineage media slots changed');
  // A derived slot is bound exactly when its source is.
  if (!compositionDeferralClosedV1(authority.spec, slots, slots.filter(slot => !actualSlots.includes(slot)))) {
    throw new Error('Composition lineage media does not cover accepted slots');
  }
  return checked.sort((left, right) => left.slot.localeCompare(right.slot));
}

export async function acceptCompositionLineageV1(
  input: CompositionLineageBodyV1,
  authority: AcceptedCompositionV1,
): Promise<CompositionLineageV1> {
  exactRecord(input, ['schemaVersion', 'kind', 'authorityDigest', 'predecessor', 'media'], 'composition lineage');
  if (input.schemaVersion !== 1 || !['fork-copy', 'asset-rebind', 'presentation-republish'].includes(input.kind)) {
    throw new Error('Unsupported composition lineage');
  }
  exactRecord(
    input.predecessor,
    ['creationId', 'version', 'jobId', 'authorityDigest', 'runtime', 'sourcePath', 'sourceDigest',
      ...(Object.hasOwn(input.predecessor, 'receiptDigest') ? ['receiptDigest'] : [])],
    'composition predecessor',
  );
  repositoryId(input.predecessor.creationId, 'composition predecessor creation');
  repositoryId(input.predecessor.jobId, 'composition predecessor job');
  nonEmpty(input.predecessor.sourcePath, 'composition predecessor source');
  if (!Number.isSafeInteger(input.predecessor.version) || input.predecessor.version < 1) {
    throw new Error('Invalid composition predecessor version');
  }
  validateSha256(input.predecessor.authorityDigest, 'composition predecessor authority');
  validateSha256(input.predecessor.sourceDigest, 'composition predecessor source');
  const runtime = validateRuntimeIdentity(input.predecessor.runtime);
  if (input.authorityDigest !== authority.digest
    || (await compositionOnRuntimeV1(authority, runtime).catch(() => null))?.digest !== input.predecessor.authorityDigest) {
    throw new Error('Composition authority changed across lineage');
  }

  exactRecord(input.media, ['kind', ...(input.media.kind === 'coded' ? [] : ['refs'])], 'composition lineage media');
  let media: CompositionLineageMediaV1;
  if (!authority.media) {
    if (input.media.kind !== 'coded' || input.predecessor.receiptDigest !== undefined) {
      throw new Error('Coded composition cannot inherit media authority');
    }
    media = { kind: 'coded' };
  } else {
    if (!input.predecessor.receiptDigest) throw new Error('Composition predecessor final receipt is required');
    validateSha256(input.predecessor.receiptDigest, 'composition predecessor receipt');
    const requiredKind = compositionLineageRetainsFinalMediaV1(input.kind) ? 'retained-final' : 'approved-variants';
    if (input.media.kind !== requiredKind) throw new Error('Composition successor media provenance is invalid');
    media = {
      kind: requiredKind,
      refs: validateRefs(input.media.refs, authority,
        input.kind === 'asset-rebind' ? input.predecessor.creationId : undefined),
    };
  }
  if (input.kind === 'asset-rebind' && media.kind === 'coded') {
    throw new Error('Coded composition has no rebindable media');
  }
  const body: CompositionLineageBodyV1 = {
    schemaVersion: 1,
    kind: input.kind,
    authorityDigest: authority.digest,
    predecessor: { ...structuredClone(input.predecessor), runtime },
    media,
  };
  return { ...body, digest: await canonicalDigest(body) };
}

export async function validateCompositionLineageV1(
  input: unknown,
  authority: AcceptedCompositionV1,
): Promise<CompositionLineageV1> {
  exactRecord(input, ['schemaVersion', 'kind', 'authorityDigest', 'predecessor', 'media', 'digest'], 'composition lineage');
  const { digest, ...body } = input;
  validateSha256(digest, 'composition lineage');
  const checked = await acceptCompositionLineageV1(body as unknown as CompositionLineageBodyV1, authority);
  if (checked.digest !== digest) throw new Error('Composition lineage digest mismatch');
  return checked;
}

/** The bound media a composed version took from elsewhere, by its own slot:
 * a fork's inherited refs, or the art its revision carried — each in the
 * namespace that owns the variant. Every other slot it binds is its own. */
export function compositionInheritedMediaV1(policy: {
  compositionLineage?: CompositionLineageV1 | undefined;
  compositionRevision?: CompositionRevisionV1 | undefined;
} | undefined): Map<string, CompositionMediaRefV1> {
  const lineage = policy?.compositionLineage;
  if (lineage) return new Map(lineage.media.kind === 'coded' ? [] : lineage.media.refs.map(ref => [ref.slot, ref]));
  return new Map((policy?.compositionRevision?.carry ?? []).map(item => [item.slot, { ...item.from, slot: item.slot }]));
}

/** A composed version proven published: its validated accepted authority,
 * the predecessor record a successor or revision binds (with the final
 * receipt digest when it carries media), the lineage it was itself built
 * under, and its final media receipt (absent for a coded game). */
export interface PublishedCompositionV1 {
  authority: AcceptedCompositionV1;
  predecessor: CompositionPredecessorV1;
  sourceLineage?: CompositionLineageV1;
  /** The revision the version was itself built as, if it was one. */
  sourceRevision?: CompositionRevisionV1;
  /** The bound media the version took from elsewhere, by its own slot: a
   * fork's inherited refs or the art its revision carried (each in the
   * namespace that owns the variant). Every other bound slot is its own. */
  inherited: Map<string, CompositionMediaRefV1>;
  receipt?: CompositionMediaReceiptV1;
}

/** THE proof that a version is a published composed game, read exclusively
 * from that version and its terminal job: live, its archive and proofs, its
 * barrier, its coded checkpoint or its final media proof and receipt, and
 * the lineage it was built under, all consistent with its accepted
 * authority. Repositories still lock and re-read these rows plus the active
 * pointer before accepting a command that builds on it. */
export async function verifyPublishedCompositionV1(input: {
  sourceCreationId: string;
  sourceVersion: CreationVersion;
  sourceJobId: string;
  sourceJob: BuildJob;
}): Promise<PublishedCompositionV1> {
  const storedAuthority = input.sourceVersion.executionPolicy?.composition;
  const proof = input.sourceVersion.gates.compositionProof;
  const archiveDigest = input.sourceVersion.gates.srcSha256;
  const run = input.sourceJob.measuredExecution;
  const barrier = run?.compositionBarrier;
  const structuralProof = barrier?.structural.candidate.report.compositionProof;
  if (!storedAuthority || input.sourceVersion.buildStatus !== 'live' || !input.sourceVersion.srcPath
    || input.sourceVersion.creationRun?.codeJobId !== input.sourceJobId
    || input.sourceJob.status !== 'done' || input.sourceJob.creationId !== input.sourceCreationId
    || input.sourceJob.version !== input.sourceVersion.n || !run || ![4, 5].includes(run.recipe.revision)
    || run.compositionDigest !== storedAuthority.digest || !proof || !archiveDigest
    || proof.authorityDigest !== storedAuthority.digest || proof.closureDigest !== storedAuthority.closureDigest
    || !barrier || barrier.authorityDigest !== storedAuthority.digest || !structuralProof
    || barrier.candidateId !== barrier.structural.candidate.candidateId
    || barrier.proofDigest !== await canonicalDigest(structuralProof)
    || barrier.structural.candidateDigest !== await canonicalDigest(barrier.structural.candidate)
    || (!storedAuthority.media && canonicalJson(structuralProof) !== canonicalJson(proof))) {
    throw new Error('Published composition predecessor proof is incomplete or changed');
  }
  validateSha256(archiveDigest, 'composition predecessor archive');
  repositoryId(input.sourceCreationId, 'composition predecessor creation');
  assertPublishedSourceArchive(input.sourceVersion.srcPath, input.sourceCreationId, input.sourceVersion.n, archiveDigest);
  const authority = await validateAcceptedCompositionV1(storedAuthority);
  const sourceLineage = input.sourceVersion.executionPolicy?.compositionLineage
    ? await validateCompositionLineageV1(input.sourceVersion.executionPolicy.compositionLineage, authority)
    : undefined;
  if (run?.compositionLineageDigest !== sourceLineage?.digest) {
    throw new Error('Published composition predecessor lineage changed');
  }
  const sourceRevision = input.sourceVersion.executionPolicy?.compositionRevision
    ? await validateCompositionRevisionV1(input.sourceVersion.executionPolicy.compositionRevision, authority)
    : undefined;
  if (run?.compositionRevisionDigest !== sourceRevision?.digest) {
    throw new Error('Published composition predecessor revision changed');
  }
  const inherited = compositionInheritedMediaV1({ compositionLineage: sourceLineage, compositionRevision: sourceRevision });
  const sources = { ...(sourceLineage ? { sourceLineage } : {}), ...(sourceRevision ? { sourceRevision } : {}), inherited };
  const predecessor: CompositionPredecessorV1 = {
    creationId: input.sourceCreationId,
    version: input.sourceVersion.n,
    jobId: input.sourceJobId,
    authorityDigest: authority.digest,
    runtime: compositionRuntimeIdentityV1(authority),
    sourcePath: input.sourceVersion.srcPath,
    sourceDigest: proof.sourceDigest,
  };
  if (!authority.media) {
    const checkpoint = run?.checkpoint;
    if (checkpoint?.revision !== barrier.checkpointRevision || checkpoint.currentCandidateId !== barrier.candidateId
      || checkpoint.bestCandidateId !== barrier.candidateId
      || barrier.structural.candidateDigest !== await canonicalDigest(checkpoint.candidates[barrier.candidateId] ?? null)
      || archiveDigest !== barrier.structural.candidate.source.sha256) {
      throw new Error('Published composition predecessor coded checkpoint is incomplete or changed');
    }
    return { authority, predecessor, ...sources };
  }
  const final = run.compositionFinalProof;
  const checkpoint = run.checkpoint;
  if (!final || final.authorityDigest !== authority.digest
    || final.structuralProofDigest !== barrier.proofDigest
    || final.mediaBasisDigest !== barrier.mediaBasisDigest
    || final.candidateId === barrier.candidateId || final.candidateId !== final.candidate.candidateId
    || final.candidateDigest !== await canonicalDigest(final.candidate)
    || checkpoint?.revision !== final.checkpointRevision
    || checkpoint.currentCandidateId !== final.candidateId || checkpoint.bestCandidateId !== final.candidateId
    || final.candidateDigest !== await canonicalDigest(checkpoint.candidates[final.candidateId] ?? null)
    || canonicalJson(final.candidate.report.compositionProof) !== canonicalJson(proof)
    || archiveDigest !== final.candidate.source.sha256
    || final.receiptDigest !== await canonicalDigest(final.receipt)
    || final.receiptDigest !== await canonicalDigest(final.candidate.report.compositionMedia)
    || final.receipt.authorityDigest !== authority.digest
    || final.receipt.closureDigest !== authority.closureDigest
    || final.receipt.mediaBasisDigest !== barrier.mediaBasisDigest
    || final.receipt.structuralSourceDigest !== (structuralProof as { sourceDigest?: string }).sourceDigest
    || !compositionReceiptCoversV1(final.receipt, authority.media.slots, authority.spec)) {
    throw new Error('Published composition predecessor media proof is incomplete or changed');
  }
  predecessor.receiptDigest = final.receiptDigest;
  return { authority, predecessor, ...sources, receipt: final.receipt };
}

/** The media a published composed version binds, one per bound slot
 * (sorted): each receipt item's variant, in the namespace that owns it — the
 * one a fork inherited it from or a revision carried it from, else the
 * version's own creation. Items the
 * receipt carries without a variant identity are not referenceable. */
export function publishedCompositionMediaV1(published: PublishedCompositionV1):
  Array<Omit<CompositionMediaRefV1, 'url'>> {
  if (!published.receipt) return [];
  return compositionReceiptItemsV1(published.receipt).flatMap(item => item.variantId ? [{
    slot: item.slot, creationId: published.inherited.get(item.slot)?.creationId ?? published.predecessor.creationId,
    variantId: item.variantId, contentSha256: item.contentSha256,
  }] : []).sort((left, right) => left.slot.localeCompare(right.slot));
}

/** Derive successor lineage exclusively from an already published composed
 * version and its terminal job ({@link verifyPublishedCompositionV1}), for the
 * successor's admitted `authority` (its predecessor's accepted game on the
 * current release, from {@link admitCompositionSuccessorV1}). */
export async function deriveCompositionLineageV1(input: {
  kind: CompositionLineageV1['kind'];
  authority: AcceptedCompositionV1;
  sourceCreationId: string;
  sourceVersion: CreationVersion;
  sourceJobId: string;
  sourceJob: BuildJob;
  mediaRefs?: CompositionMediaRefV1[];
}): Promise<{ authority: AcceptedCompositionV1; lineage: CompositionLineageV1 }> {
  const { authority, predecessor, inherited: inheritedBySlot, receipt } = await verifyPublishedCompositionV1(input);
  const successor = await validateAcceptedCompositionV1(input.authority);
  if (!await isCompositionSuccessorV1(successor, authority)) {
    throw new Error('Composition successor authority is not its predecessor\'s accepted game');
  }
  let media: CompositionLineageMediaV1;
  if (!authority.media || !receipt) {
    media = { kind: 'coded' };
  } else {
    const refs = input.mediaRefs ?? [];
    if (compositionLineageRetainsFinalMediaV1(input.kind)) {
      const bySlot = new Map(compositionReceiptItemsV1(receipt).map(item => [item.slot, item]));
      for (const ref of refs) {
        const prior = bySlot.get(ref.slot);
        const inherited = inheritedBySlot.get(ref.slot);
        if (!prior || ref.creationId !== (inherited?.creationId ?? input.sourceCreationId)
          || ref.variantId !== prior.variantId || ref.variantId !== (inherited?.variantId ?? prior.variantId)
          || ref.contentSha256 !== prior.contentSha256
          || ref.contentSha256 !== (inherited?.contentSha256 ?? prior.contentSha256)
          || (inherited && ref.url !== inherited.url)) {
          throw new Error('Retained media reference does not match predecessor final receipt');
        }
      }
    }
    // The bundle slots a successor leaves unbound are slots its predecessor
    // deferred: a fork inherits exactly that gap; a rebind may fill any of it.
    const bundle = compositionBundleSlotsV1(authority.media.slots);
    const unbound = bundle.filter(slot => !refs.some(ref => ref.slot === slot)).sort();
    const deferred = (receipt.deferred ?? []).map(item => item.slot);
    if (compositionLineageRetainsFinalMediaV1(input.kind) ? canonicalJson(unbound) !== canonicalJson(deferred)
      : unbound.some(slot => !deferred.includes(slot))) {
      throw new Error('Composition successor media does not cover what its predecessor bound');
    }
    media = { kind: compositionLineageRetainsFinalMediaV1(input.kind) ? 'retained-final' : 'approved-variants', refs };
  }
  return {
    authority: successor,
    lineage: await acceptCompositionLineageV1({ schemaVersion: 1, kind: input.kind, authorityDigest: successor.digest,
      predecessor, media }, successor),
  };
}

/** The live version a composed revision replaces as its creation's game. A
 * composed@1 predecessor names its accepted authority and, when it carries
 * media, its final receipt; a GENERIC predecessor (a live game converted onto
 * the composed builder) names neither and carries no art. */
export interface CompositionRevisionPredecessorV1 {
  creationId: string;
  version: number;
  /** The predecessor's code job; null only for a generic version built
   * before creation runs existed. */
  jobId: string | null;
  authorityDigest: string | null;
  receiptDigest: string | null;
}

/** Immutable authority of a composed REVISION build
 * (`executionPolicy.compositionRevision`): a NEW accepted game
 * (`authorityDigest`, the build's own executionPolicy.composition, admitted
 * fresh like a Create) that replaces `predecessor` on the same creation, and
 * the art it carries from that predecessor's published media
 * ({@link compositionRevisionCarryV1}). Unlike lineage it never claims the
 * game is unchanged. */
export interface CompositionRevisionV1 {
  schemaVersion: 1;
  kind: 'composed-revision';
  authorityDigest: string;
  predecessor: CompositionRevisionPredecessorV1;
  carry: CompositionMediaCarryV1[];
  digest: string;
}

type CompositionRevisionBodyV1 = Omit<CompositionRevisionV1, 'digest'>;

const CARRY_REASONS: readonly CompositionMediaCarryV1['reason'][] = ['same-basis', 'creator-attached'];

function validateMediaRef(input: unknown, label: string): CompositionMediaRefV1 {
  exactRecord(input, ['slot', 'creationId', 'variantId', 'contentSha256', 'url'], label);
  nonEmpty(input.slot, `${label} slot`);
  repositoryId(input.creationId, `${label} creation`);
  repositoryId(input.variantId, `${label} variant`);
  nonEmpty(input.url, `${label} URL`);
  validateSha256(input.contentSha256, label);
  return structuredClone(input) as unknown as CompositionMediaRefV1;
}

export async function acceptCompositionRevisionV1(
  input: CompositionRevisionBodyV1,
  authority: AcceptedCompositionV1,
): Promise<CompositionRevisionV1> {
  exactRecord(input, ['schemaVersion', 'kind', 'authorityDigest', 'predecessor', 'carry'], 'composition revision');
  if (input.schemaVersion !== 1 || input.kind !== 'composed-revision') throw new Error('Unsupported composition revision');
  if (input.authorityDigest !== authority.digest || authority.spec.profile !== COMPOSED_PROFILE) {
    throw new Error('Composition revision does not name its revised composed game');
  }
  exactRecord(input.predecessor, ['creationId', 'version', 'jobId', 'authorityDigest', 'receiptDigest'], 'composition revision predecessor');
  const predecessor = input.predecessor;
  repositoryId(predecessor.creationId, 'composition revision predecessor creation');
  if (!Number.isSafeInteger(predecessor.version) || predecessor.version < 1) throw new Error('Invalid composition revision predecessor version');
  if (predecessor.jobId !== null) repositoryId(predecessor.jobId, 'composition revision predecessor job');
  if (predecessor.authorityDigest !== null) validateSha256(predecessor.authorityDigest, 'composition revision predecessor authority');
  if (predecessor.receiptDigest !== null) validateSha256(predecessor.receiptDigest, 'composition revision predecessor receipt');
  if (predecessor.authorityDigest === null && predecessor.receiptDigest !== null) {
    throw new Error('A generic composition revision predecessor has no media receipt');
  }
  if (!Array.isArray(input.carry)) throw new Error('Invalid composition revision carry');
  // A composed predecessor carries from its published media; a generic one
  // only its attached assets.
  if (input.carry.length && (!authority.media || (predecessor.authorityDigest !== null && predecessor.receiptDigest === null))) {
    throw new Error('Composition revision carries art from a predecessor without published media');
  }
  const carryable = new Set(compositionSlotBasisV1(authority).map(row => row.slot));
  const carry = input.carry.map((item): CompositionMediaCarryV1 => {
    exactRecord(item, ['slot', 'from', 'reason'], 'composition revision carry');
    if (typeof item.slot !== 'string' || !carryable.has(item.slot)) throw new Error('Invalid composition revision carry slot');
    if (!CARRY_REASONS.includes(item.reason as CompositionMediaCarryV1['reason'])) throw new Error('Invalid composition revision carry reason');
    return { slot: item.slot, from: validateMediaRef(item.from, 'composition revision carried media'),
      reason: item.reason as CompositionMediaCarryV1['reason'] };
  });
  const slots = carry.map(item => item.slot);
  if (new Set(slots).size !== slots.length || canonicalJson(slots) !== canonicalJson([...slots].sort())) {
    throw new Error('Composition revision carry slots changed');
  }
  // A strip is carried only with the sprite that anchors it.
  for (const strip of compositionAnimationSlotsV1(authority.spec)) {
    if (slots.includes(strip.slot) && !slots.includes(strip.kind)) throw new Error('Composition revision carries a strip without its sprite');
  }
  const body: CompositionRevisionBodyV1 = { schemaVersion: 1, kind: 'composed-revision', authorityDigest: authority.digest,
    predecessor: structuredClone(predecessor), carry };
  return { ...body, digest: await canonicalDigest(body) };
}

export async function validateCompositionRevisionV1(input: unknown, authority: AcceptedCompositionV1): Promise<CompositionRevisionV1> {
  exactRecord(input, ['schemaVersion', 'kind', 'authorityDigest', 'predecessor', 'carry', 'digest'], 'composition revision');
  const { digest, ...body } = input;
  validateSha256(digest, 'composition revision');
  const checked = await acceptCompositionRevisionV1(body as unknown as CompositionRevisionBodyV1, authority);
  if (checked.digest !== digest) throw new Error('Composition revision digest mismatch');
  return checked;
}

/** Derive a revision's authority exclusively from the live version it
 * replaces. A composed@1 predecessor must be a published composed game
 * ({@link verifyPublishedCompositionV1}) — of the same template, or another
 * one when the revision took the full Create route — and every
 * reference `readMedia` returns must be exactly one of its published
 * variants ({@link publishedCompositionMediaV1}); the carry is then THE policy
 * ({@link compositionRevisionCarryV1}). A generic predecessor is a live
 * version without composition authority: its carry is its attached assets
 * (its assets snapshot) the new game has slots for. Repositories lock
 * and re-read these rows and the active pointer before accepting. */
/** Every reference `readMedia` returned is exactly one of the bindings asked for, once. */
function assertBoundTo(bound: ReadonlyArray<{ ref: CompositionMediaRefV1 }>, asked: ReadonlyArray<Omit<CompositionMediaRefV1, 'url'>>): void {
  const bindings = new Map(asked.map(binding => [binding.slot, binding]));
  const seen = new Set<string>();
  for (const { ref } of bound) {
    const binding = bindings.get(ref.slot);
    validateMediaRef(ref, 'composition revision predecessor media');
    if (!binding || seen.has(ref.slot) || binding.creationId !== ref.creationId || binding.variantId !== ref.variantId
      || binding.contentSha256 !== ref.contentSha256) throw new Error('Composition revision media is not its predecessor\'s published media');
    seen.add(ref.slot);
  }
}

export async function deriveCompositionRevisionV1(input: {
  authority: AcceptedCompositionV1;
  sourceCreationId: string;
  sourceVersion: CreationVersion;
  /** The predecessor's terminal code job; null for a generic predecessor. */
  sourceJob: BuildJob | null;
  /** Reads the predecessor's published variants ({@link publishedCompositionMediaV1})
   * the caller can still carry — each with its variant URL and origin
   * ({@link compositionMediaOriginV1}); an unavailable or rejected variant is
   * left out and regenerated. A generic predecessor's bindings are its
   * assets snapshot. */
  readMedia: (bindings: Array<Omit<CompositionMediaRefV1, 'url'>>) =>
    Promise<ReadonlyArray<{ ref: CompositionMediaRefV1; origin: 'generated' | 'attached'; kind: string }>>;
}): Promise<CompositionRevisionV1> {
  const revised = await validateAcceptedCompositionV1(input.authority);
  if (revised.spec.profile !== COMPOSED_PROFILE) throw new Error('A composition revision builds a composed game');
  repositoryId(input.sourceCreationId, 'composition revision predecessor creation');
  if (!input.sourceVersion.executionPolicy?.composition) {
    if (input.sourceVersion.buildStatus !== 'live' || !input.sourceVersion.srcPath) {
      throw new Error('Generic composition revision predecessor is not a live game');
    }
    // Its attached assets (the version's snapshot) are its art: each still
    // available one whose name and kind is a slot of the new game is kept.
    // A tint-neutral kind's art is never kept: it is grayscale for the old
    // shell's runtime tint, which a composed game does not apply (its strips
    // follow their sprite out of the carry).
    const design = input.sourceVersion.designSnapshot;
    const neutral = new Set(design ? artPlanAssetNames(design)
      .filter(asset => asset.kind === 'sprite' && design.entities[asset.index]?.sprite?.tint === 'neutral').map(asset => asset.name) : []);
    const attached = (input.sourceVersion.assetsSnapshot ?? []).filter(asset => asset.contentSha256 && !neutral.has(asset.name))
      .map(asset => ({ slot: asset.name, creationId: input.sourceCreationId, variantId: asset.variantId, contentSha256: asset.contentSha256! }));
    const bound = attached.length ? await input.readMedia(attached) : [];
    assertBoundTo(bound, attached);
    return acceptCompositionRevisionV1({ schemaVersion: 1, kind: 'composed-revision', authorityDigest: revised.digest,
      predecessor: { creationId: input.sourceCreationId, version: input.sourceVersion.n,
        jobId: input.sourceVersion.creationRun?.codeJobId ?? null, authorityDigest: null, receiptDigest: null },
      carry: compositionRevisionCarryV1({ predecessor: null, revised, bound }) }, revised);
  }
  const sourceJobId = input.sourceVersion.creationRun?.codeJobId;
  if (!sourceJobId || !input.sourceJob) throw new Error('Published composition predecessor proof is incomplete or changed');
  const published = await verifyPublishedCompositionV1({ sourceCreationId: input.sourceCreationId, sourceVersion: input.sourceVersion,
    sourceJobId, sourceJob: input.sourceJob });
  const prior = published.authority.spec;
  if (prior.profile !== COMPOSED_PROFILE) throw new Error('A composition revision replaces a composed game');
  const media = publishedCompositionMediaV1(published);
  const bound = await input.readMedia(media);
  assertBoundTo(bound, media);
  return acceptCompositionRevisionV1({ schemaVersion: 1, kind: 'composed-revision', authorityDigest: revised.digest,
    predecessor: { creationId: input.sourceCreationId, version: input.sourceVersion.n, jobId: sourceJobId,
      authorityDigest: published.authority.digest, receiptDigest: published.predecessor.receiptDigest ?? null },
    carry: compositionRevisionCarryV1({ predecessor: published.authority, revised, bound }) }, revised);
}
